import { FC, ReactNode } from 'react';
import { IconButton } from 'shared/components/IconButton/IconButton';
import { LoadingSpinner } from 'shared/components/LoadingSpinner/LoadingSpinner';
import { CircleFilledCheckIcon, CrossIcon, InfoIconFilled } from 'shared/icons';
import { IconType } from 'shared/icons/SvgIcon';
import { mergeClasses } from 'shared/utils/mergeClasses';

export type Icon = 'check' | 'info';
export type Color = 'primary' | 'success' | 'warning';

const getVariantClassNames = (color: Color) => {
  switch (color) {
    case 'primary':
      return '[&_.icon]:text-primary [&_.bar]:bg-primary';
    case 'success':
      return '[&_.icon]:text-green-700 [&_.bar]:bg-green-700 [&_.spinner]:border-r-green-700 [&_.spinner]:border-t-green-700';
    case 'warning':
      return '[&_.icon]:text-orange [&_.bar]:bg-orange [&_.spinner]:border-r-orange [&_.spinner]:border-t-orange';
    default:
      return '';
  }
};

const iconDictionary: Record<Icon, IconType> = {
  check: CircleFilledCheckIcon,
  info: InfoIconFilled,
};

const getIconComponent = (icon?: Icon, loading?: boolean) => {
  if (loading) {
    return <LoadingSpinner size={20} className="min-w-5" />;
  }
  if (icon) {
    const Icon = iconDictionary[icon];
    return <Icon className="icon h-6 w-6 min-w-6" />;
  }
  return null;
};

interface ToastItemProps {
  title: string;
  message: ReactNode;
  icon?: Icon;
  loading?: boolean;
  color?: Color;
  closeable?: boolean;
  onClose: () => void;
  className?: string;
}

export const ToastItem: FC<ToastItemProps> = ({
  title,
  message,
  icon,
  onClose,
  loading = false,
  closeable = true,
  color = 'primary',
  className = '',
}) => {
  return (
    <div
      className={mergeClasses(
        'flex w-[394px] max-w-screen justify-between gap-6 p-1 pr-3',
        className,
        getVariantClassNames(color),
      )}
    >
      <div className="flex gap-3">
        <div className="bar h-full w-1.5 min-w-1.5 rounded-md" />
        <div className="flex gap-2.5 py-1">
          {getIconComponent(icon, loading)}
          <div className="space-y-1">
            <p className="font-bold text-black">{title}</p>
            <p className="text-xs text-gray">{message}</p>
          </div>
        </div>
      </div>
      {closeable && (
        <IconButton
          className="min-w-5"
          size="sm"
          name="closeToastButton"
          icon={CrossIcon}
          onClick={onClose}
          variant="gray"
        />
      )}
    </div>
  );
};
